import { Fragment, useState } from 'react';
import type { Badge, Quote, RateShopResult } from '../types';
import { money, percent, shortDate, signedMoney, weight } from '../lib/format';

/**
 * Every badge pairs its colour with a written label - status colour never
 * carries the meaning on its own.
 */
const BADGE_META: Record<Badge, { label: string; tone: string; title: string }> = {
  cheapest: {
    label: 'Lowest cost',
    tone: 'good',
    title: 'Cheapest service that still meets the required-by date',
  },
  fastest: {
    label: 'Earliest arrival',
    tone: 'accent',
    title: 'Arrives soonest, counting the commit time as well as the date',
  },
  'misses-deadline': {
    label: 'Misses deadline',
    tone: 'critical',
    title: 'Delivers after the required-by date on this load',
  },
  dominated: {
    label: 'Never worth it',
    tone: 'warning',
    title: 'Another service on this list costs less AND arrives earlier',
  },
};

interface Props {
  result: RateShopResult;
  selectedShipVia: string | null;
  busyShipVia: string | null;
  onSelect: (quote: Quote) => void;
  onBook: (quote: Quote) => void;
  canBook: boolean;
}

export function RateTable({ result, selectedShipVia, busyShipVia, onSelect, onBook, canBook }: Props) {
  const [expanded, setExpanded] = useState<string | null>(null);

  const maxRate = Math.max(...result.quotes.map((q) => q.rate), 0);

  return (
    <div className="table-scroll">
      <table className="rates">
        <caption className="sr-only">
          Rate shop results for load {result.loadId}, cheapest first
        </caption>
        <thead>
          <tr>
            <th scope="col">Service</th>
            <th scope="col" className="num">
              Cost
            </th>
            <th scope="col">Relative cost</th>
            <th scope="col">Arrives</th>
            <th scope="col" className="num">
              Transit
            </th>
            <th scope="col" className="num">
              Billed wt
            </th>
            <th scope="col" className="num">
              Off list
            </th>
            <th scope="col" aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {result.quotes.map((q) => {
            const isOpen = expanded === q.shipVia;
            const isChosen = selectedShipVia === q.shipVia;
            const isBusy = busyShipVia === q.shipVia;
            const faded = !q.meetsDeadline || Boolean(q.dominatedBy);
            const barPct = maxRate > 0 ? Math.max(2, (q.rate / maxRate) * 100) : 0;
            const isCheapest = q.badges.includes('cheapest');

            return (
              <Fragment key={q.shipVia}>
                <tr
                  className={[isChosen ? 'chosen' : '', faded ? 'faded' : ''].filter(Boolean).join(' ')}
                >
                  <td className="keep">
                    <div className="svc">
                      <span className="name">{q.serviceName}</span>
                      <span className="code">
                        {q.shipVia}
                        {q.zone ? ` · zone ${q.zone}` : ''}
                        {q.carrier ? ` · ${q.carrier}` : ''}
                      </span>
                      {q.badges.length > 0 && (
                        <span className="badges">
                          {q.badges.map((b) => (
                            <span key={b} className={`chip ${BADGE_META[b].tone}`} title={BADGE_META[b].title}>
                              <span className="dot" aria-hidden="true" />
                              {BADGE_META[b].label}
                              {b === 'dominated' && q.dominatedBy ? ` (${q.dominatedBy} wins)` : ''}
                            </span>
                          ))}
                        </span>
                      )}
                    </div>
                  </td>

                  <td className="num keep">
                    <div className="rate-cell">{money(q.rate)}</div>
                    {q.listTotal !== null && <div className="sub-note">list {money(q.listTotal)}</div>}
                  </td>

                  <td>
                    <span
                      className={`cost-bar${isCheapest ? ' is-cheapest' : ''}`}
                      role="img"
                      aria-label={`${money(q.rate)}, ${Math.round(barPct)}% of the most expensive option`}
                    >
                      <span style={{ width: `${barPct}%` }} />
                    </span>
                  </td>

                  <td className="keep">
                    <div>{shortDate(q.deliveryDate)}</div>
                    <div className="sub-note">by {q.commitTime}</div>
                  </td>

                  <td className="num">
                    {q.transitDays}d
                    {q.daysEarly !== null && q.meetsDeadline && (
                      <div className="sub-note">
                        {q.daysEarly === 0 ? 'on deadline' : `${q.daysEarly}d spare`}
                      </div>
                    )}
                  </td>

                  <td className="num">
                    {weight(q.billedWeight)}
                    {q.dimApplied && (
                      <div className="sub-note" title={`Dimensional weight ${q.dimWeight ?? '?'} lb exceeds the ${q.actualWeight} lb scale weight`}>
                        DIM (scale {q.actualWeight})
                      </div>
                    )}
                  </td>

                  <td className="num">
                    {percent(q.savingsPct)}
                    {q.savings !== null && <div className="sub-note">saves {money(q.savings)}</div>}
                  </td>

                  <td className="keep">
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="btn small"
                        onClick={() => setExpanded(isOpen ? null : q.shipVia)}
                        aria-expanded={isOpen}
                      >
                        {isOpen ? 'Hide' : 'Charges'}
                      </button>
                      <button
                        type="button"
                        className={`btn small${isChosen ? '' : ' primary'}`}
                        disabled={isBusy}
                        onClick={() => (canBook ? onBook(q) : onSelect(q))}
                      >
                        {isBusy && <span className="spinner" />}
                        {isChosen ? 'Chosen' : canBook ? 'Book' : 'Select'}
                      </button>
                    </div>
                  </td>
                </tr>

                {isOpen && (
                  <tr className="breakdown">
                    <td colSpan={8}>
                      <div className="breakdown-inner">
                        <div>
                          <h4>Charge breakdown</h4>
                          <div className="charge-list">
                            {q.charges.map((c) => (
                              <div className="charge-row" key={c.id}>
                                <span>{c.label}</span>
                                <span className={c.value < 0 ? 'neg' : ''}>{signedMoney(c.value)}</span>
                              </div>
                            ))}
                            <div className="charge-row total">
                              <span>Total</span>
                              <span>{money(q.rate)}</span>
                            </div>
                          </div>
                        </div>

                        <div>
                          <h4>How the rate was built</h4>
                          <div className="charge-list">
                            <div className="charge-row">
                              <span>Base charge</span>
                              <span>{money(q.baseCharge)}</span>
                            </div>
                            <div className="charge-row">
                              <span>Negotiated discount{q.discountPct !== null ? ` (${percent(q.discountPct)})` : ''}</span>
                              <span className="neg">{signedMoney(q.discount)}</span>
                            </div>
                            <div className="charge-row">
                              <span>Net base</span>
                              <span>{money(q.netBase)}</span>
                            </div>
                            <div className="charge-row">
                              <span>Accessorials</span>
                              <span>{money(q.ssCharge)}</span>
                            </div>
                            <div className="charge-row">
                              <span>Fuel</span>
                              <span>{money(q.fuelCharge)}</span>
                            </div>
                          </div>
                        </div>

                        <div>
                          <h4>Rating basis</h4>
                          <div className="charge-list">
                            <div className="charge-row">
                              <span>Scale weight</span>
                              <span>{weight(q.actualWeight)}</span>
                            </div>
                            <div className="charge-row">
                              <span>Dimensional weight</span>
                              <span>{q.dimWeight !== null ? weight(q.dimWeight) : '—'}</span>
                            </div>
                            <div className="charge-row">
                              <span>Billed on</span>
                              <span>{q.dimApplied ? 'cube' : 'scale'}</span>
                            </div>
                            <div className="charge-row">
                              <span>Zone</span>
                              <span>{q.zone || '—'}</span>
                            </div>
                            <div className="charge-row">
                              <span>Discount type</span>
                              <span>{q.discountType || '—'}</span>
                            </div>
                            <div className="charge-row">
                              <span>Rate engine</span>
                              <span>{q.rateEngine || '—'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
